import {
  Bell,
  Car,
  LayoutDashboard,
  LifeBuoy,
  Settings,
  ShieldAlert,
  Truck,
  Wrench,
  UserCog,
  PackagePlus,
  HardDrive,
  Gauge,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Role } from '@/types/api';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: readonly Role[];
  section: 'main' | 'console';
}

const ALL: readonly Role[] = [
  'vehicle_owner',
  'fleet_manager',
  'emergency_responder',
  'administrator',
];

/**
 * Navigation and route guards read this same list, so a link can never appear for a
 * role the router will bounce — the previous app had no role gating at all and every
 * signed-in user could reach every page.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/overview',
    label: 'Overview',
    icon: LayoutDashboard,
    roles: ['vehicle_owner', 'fleet_manager', 'administrator'],
    section: 'main',
  },
  {
    to: '/vehicles',
    label: 'Vehicles',
    icon: Car,
    roles: ['vehicle_owner', 'fleet_manager', 'administrator'],
    section: 'main',
  },
  {
    to: '/crashes',
    label: 'Crash events',
    icon: ShieldAlert,
    roles: ['vehicle_owner', 'fleet_manager', 'administrator'],
    section: 'main',
  },
  {
    to: '/alerts',
    label: 'Alerts',
    icon: Bell,
    roles: ALL,
    section: 'main',
  },
  {
    to: '/fleet',
    label: 'Fleet',
    icon: Truck,
    roles: ['fleet_manager', 'administrator'],
    section: 'main',
  },
  {
    to: '/respond',
    label: 'Response queue',
    icon: LifeBuoy,
    roles: ['emergency_responder', 'administrator'],
    section: 'main',
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Settings,
    roles: ALL,
    section: 'main',
  },
];

/**
 * The internal operations console. A separate list, not a section of the customer
 * nav: an administrator runs the product rather than owning a vehicle in it, so
 * these are their primary navigation, not an appendix to somebody else's.
 */
export const CONSOLE_ITEMS: readonly NavItem[] = [
  {
    to: '/admin',
    label: 'Operations',
    icon: Gauge,
    roles: ['administrator'],
    section: 'console',
  },
  {
    to: '/admin/devices',
    label: 'Device registry',
    icon: HardDrive,
    roles: ['administrator'],
    section: 'console',
  },
  {
    to: '/admin/provisioning',
    label: 'Provisioning',
    icon: PackagePlus,
    roles: ['administrator'],
    section: 'console',
  },
  {
    to: '/admin/customers',
    label: 'Customers',
    icon: Users,
    roles: ['administrator'],
    section: 'console',
  },
  {
    to: '/admin/roles',
    label: 'Roles',
    icon: UserCog,
    roles: ['administrator'],
    section: 'console',
  },
  {
    to: '/admin/maintenance',
    label: 'Maintenance',
    icon: Wrench,
    roles: ['administrator'],
    section: 'console',
  },
];

export function navFor(role: Role | null, section: NavItem['section']): NavItem[] {
  if (!role) return [];
  return NAV_ITEMS.filter((item) => item.section === section && item.roles.includes(role));
}

export function consoleNav(): NavItem[] {
  return [...CONSOLE_ITEMS];
}

/**
 * Where each role starts. Responders have no device access, so alerts are their home.
 *
 * Administrators land in the operations console, not on /overview — that page asks
 * "is *my* device reporting, where is *my* vehicle", which a SafeSphere operator who
 * owns no vehicle cannot answer and should not be looking at first.
 */
export function homeFor(role: Role | null): string {
  switch (role) {
    case 'emergency_responder':
      return '/respond';
    case 'fleet_manager':
      return '/fleet';
    case 'administrator':
      return '/admin';
    case 'vehicle_owner':
      return '/overview';
    default:
      return '/login';
  }
}
